import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../supabaseClient';

interface Organization {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  settings: Record<string, any>;
  subscription_tier: string;
  created_at: string;
  updated_at: string;
}

interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'technician' | 'viewer';
  joined_at: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  userOrganizations: Organization[];
  userRole: string | null;
  loading: boolean;
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  isOwner: boolean;
  isAdmin: boolean;
  showSelectorOnLoad: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSelectorOnLoad, setShowSelectorOnLoad] = useState(false);

  // Fetch user's organizations
  const fetchOrganizations = async () => {
    if (!user) {
      setUserOrganizations([]);
      setCurrentOrganization(null);
      setUserRole(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch all organizations the user is a member of
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members')
        .select(`
          id,
          organization_id,
          user_id,
          role,
          joined_at,
          organizations (
            id,
            name,
            slug,
            owner_id,
            settings,
            subscription_tier,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user.id);

      if (membershipsError) {
        console.error('Error fetching organizations:', membershipsError);
        return;
      }

      if (memberships && memberships.length > 0) {
        const orgs = memberships
          .map((m: any) => m.organizations)
          .filter(Boolean) as Organization[];

        setUserOrganizations(orgs);

        // Get the stored organization ID from localStorage
        const storedOrgId = localStorage.getItem('currentOrganizationId');

        // Find the organization to set as current
        let selectedOrg: Organization | null = null;

        // Check if user just logged in (session storage flag)
        const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
        
        // Logic for selecting organization:
        // 1. Single org -> always auto-select
        // 2. Multiple orgs + stored org ID exists -> use stored (regardless of justLoggedIn, since user may have selected it)
        // 3. Multiple orgs + no stored org ID + fresh login -> show selector
        // 4. Multiple orgs + no stored org ID + not fresh login -> show selector
        
        if (orgs.length === 1) {
          // Single organization - auto-select it
          selectedOrg = orgs[0];
          sessionStorage.removeItem('justLoggedIn'); // Clear flag
          setShowSelectorOnLoad(false);
        } else if (storedOrgId) {
          // Multiple orgs - if there's a stored org ID, use it (user previously selected it)
          selectedOrg = orgs.find(org => org.id === storedOrgId) || null;
          
          // If stored org ID doesn't match any org, clear it and show selector
          if (!selectedOrg) {
            console.log('⚠️ Stored org ID not found in user organizations, clearing it');
            localStorage.removeItem('currentOrganizationId');
            selectedOrg = null;
            setShowSelectorOnLoad(justLoggedIn);
          } else {
            // Valid stored org found - use it
            setShowSelectorOnLoad(false);
            // Clear justLoggedIn since we're using stored selection
            if (justLoggedIn) {
              sessionStorage.removeItem('justLoggedIn');
            }
          }
        } else if (orgs.length > 1) {
          // Multiple orgs but no stored selection - show selector
          selectedOrg = null;
          // Only show selector on load if it's a fresh login
          setShowSelectorOnLoad(justLoggedIn);
          if (justLoggedIn) {
            // Keep justLoggedIn flag for Layout to use, but switchOrganization will clear it
          }
        }

        if (selectedOrg) {
          setCurrentOrganization(selectedOrg);
          localStorage.setItem('currentOrganizationId', selectedOrg.id);

          // Set user role for the current organization
          const membership = memberships.find(
            (m: any) => m.organization_id === selectedOrg.id
          );
          if (membership) {
            setUserRole(membership.role);
          } else {
            // Fallback: If no membership found, check if user is the owner
            // This handles edge cases where membership might be missing
            if (selectedOrg.owner_id === user.id) {
              console.log('⚠️ No membership found, but user is org owner. Setting role to owner.');
              setUserRole('owner');
            } else {
              console.warn('⚠️ No membership found for selected organization. User role not set.');
              // Try to fetch membership directly as fallback
              supabase
                .from('organization_members')
                .select('role')
                .eq('organization_id', selectedOrg.id)
                .eq('user_id', user.id)
                .single()
                .then(({ data, error }) => {
                  if (!error && data) {
                    console.log('✅ Found membership via direct query:', data.role);
                    setUserRole(data.role);
                  }
                });
            }
          }
        } else {
          // No organization selected - user needs to choose
          setCurrentOrganization(null);
          setUserRole(null);
        }
      } else {
        // User has no organizations - this shouldn't happen as signup creates one
        console.warn('User has no organizations');
        setUserOrganizations([]);
        setCurrentOrganization(null);
        setUserRole(null);
      }
    } catch (error) {
      console.error('Error in fetchOrganizations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch organizations when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchOrganizations();
    } else {
      setUserOrganizations([]);
      setCurrentOrganization(null);
      setUserRole(null);
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  // Set up real-time subscription to detect when current user is removed from an organization
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    console.log('🔔 Setting up real-time subscription for user organization membership');

    const channel = supabase
      .channel(`user_organizations:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'DELETE', // Listen to DELETE events
          schema: 'public',
          table: 'organization_members',
          filter: `user_id=eq.${user.id}`, // Only for current user
        },
        (payload) => {
          console.log('📢 User removed from organization:', payload);
          // Refresh organizations when user is removed from one
          fetchOrganizations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT', // Also listen to INSERT for when user joins a new org
          schema: 'public',
          table: 'organization_members',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('📢 User added to organization:', payload);
          // Refresh organizations when user joins a new one
          fetchOrganizations();
        }
      )
      .subscribe((status) => {
        console.log('📡 Organization membership subscription status:', status);
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('🔕 Unsubscribing from organization membership channel');
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, user?.id]);

  // Switch to a different organization
  const switchOrganization = async (organizationId: string) => {
    const org = userOrganizations.find(o => o.id === organizationId);
    if (!org) {
      console.error('Organization not found');
      return;
    }

    setCurrentOrganization(org);
    localStorage.setItem('currentOrganizationId', organizationId);
    
    // Clear the showSelectorOnLoad flag since user has selected an org
    setShowSelectorOnLoad(false);
    sessionStorage.removeItem('justLoggedIn');

    // Fetch and update user role for the new organization
    const { data: membership, error } = await supabase
      .from('organization_members')
      .select('role')
      .eq('organization_id', organizationId)
      .eq('user_id', user?.id)
      .single();

    if (!error && membership) {
      setUserRole(membership.role);
    }
  };

  const refreshOrganizations = async () => {
    await fetchOrganizations();
  };

  const isOwner = userRole === 'owner';
  const isAdmin = userRole === 'owner' || userRole === 'admin';

  const value = {
    currentOrganization,
    userOrganizations,
    userRole,
    loading,
    switchOrganization,
    refreshOrganizations,
    isOwner,
    isAdmin,
    showSelectorOnLoad,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
